import type { EventType } from '../api/types';

export type ResourceRoom = 'left' | 'vip' | 'tavern';

export type Resource = {
  id: string;
  label: string;
  room: ResourceRoom;
  capacity: number;
};

export type VipLayoutMode = 'split' | 'combined';
export const DEFAULT_VIP_LAYOUT_MODE: VipLayoutMode = 'split';
export const VIP_SPLIT_RESOURCE_IDS = new Set(['table_game_5', 'table_game_6']);
export const VIP_COMBINED_RESOURCE_ID = 'table_game_7';

// IDs follow the prefixes required by docs/requirements/event-creation.md:
// - table_game* -> boardgames
// - table_ping_pong* -> ping_pong
// - activity_fencing* -> fencing
// - activity_archery* -> archery
// - activity_hiking* -> hiking
const BASE_RESOURCES: Resource[] = [
  // Tavern
  { id: 'table_game_1', label: 'Стол #1', room: 'tavern', capacity: 6 },
  { id: 'table_game_2', label: 'Стол #2', room: 'tavern', capacity: 6 },
  { id: 'table_game_3', label: 'Стол #3', room: 'tavern', capacity: 6 },

  // Left zone
  { id: 'table_game_4', label: 'Стол #4', room: 'left', capacity: 6 },
  { id: 'table_ping_pong_1', label: 'Тенис', room: 'left', capacity: 2 },
  { id: 'zone_courtyard_1', label: 'Двор', room: 'left', capacity: 50 },
  { id: 'activity_fencing_1', label: 'Походы: Фехтование', room: 'left', capacity: 20 },
  { id: 'activity_archery_1', label: 'Походы: Лук', room: 'left', capacity: 20 },
  { id: 'activity_hiking_1', label: 'Походы: Природа', room: 'left', capacity: 20 },
];

const VIP_SPLIT_RESOURCES: Resource[] = [
  { id: 'table_game_5', label: 'Стол #5', room: 'vip', capacity: 6 },
  { id: 'table_game_6', label: 'Стол #6', room: 'vip', capacity: 6 },
];

const VIP_COMBINED_RESOURCES: Resource[] = [
  { id: 'table_game_7', label: 'Стол VIP', room: 'vip', capacity: 12 },
];

const ALL_RESOURCES: Resource[] = [...BASE_RESOURCES, ...VIP_SPLIT_RESOURCES, ...VIP_COMBINED_RESOURCES];

export const RESOURCES: Resource[] = [
  ...BASE_RESOURCES,
  ...VIP_SPLIT_RESOURCES,
  ...VIP_COMBINED_RESOURCES,
];

const LEGACY_RESOURCE_ALIASES: Record<string, string> = {
  zone_fencing_1: 'activity_fencing_1',
};

export function normalizeResourceId(resourceId: string): string {
  const normalized = resourceId.trim().toLowerCase();
  return LEGACY_RESOURCE_ALIASES[normalized] ?? normalized;
}

export function hasResource(resourceId: string): boolean {
  const normalized = normalizeResourceId(resourceId);
  return RESOURCES.some((r) => r.id === normalized);
}

export function isKnownResource(resourceId: string): boolean {
  const normalized = normalizeResourceId(resourceId);
  return ALL_RESOURCES.some((r) => r.id === normalized);
}

export function getDefaultResourceId(): string {
  return RESOURCES[0]?.id ?? '';
}

export function getResourceCapacity(resourceId: string, fallback = 5): number {
  const normalized = normalizeResourceId(resourceId);
  return RESOURCES.find((r) => r.id === normalized)?.capacity ?? fallback;
}

export const DEFAULT_MAX_PARTICIPANTS = 20;

export function getResourceMaxParticipants(resourceId: string): number {
  return Math.max(DEFAULT_MAX_PARTICIPANTS, getResourceCapacity(resourceId, 0));
}

export function getResourcesByRoom(room: ResourceRoom): Resource[] {
  return RESOURCES.filter((r) => r.room === room);
}

export function getEventTypeByResourceId(resourceId: string): EventType {
  const normalized = normalizeResourceId(resourceId);
  if (normalized.startsWith('table_game')) return 'boardgames';
  if (normalized.startsWith('table_ping_pong')) return 'ping_pong';
  if (normalized.startsWith('zone_courtyard')) return 'courtyard';
  if (normalized.startsWith('activity_fencing')) return 'fencing';
  if (normalized.startsWith('activity_archery')) return 'archery';
  if (normalized.startsWith('activity_hiking')) return 'hiking';
  return 'boardgames';
}

export function getResourceLabel(resourceId: string): string {
  const normalized = normalizeResourceId(resourceId);
  return ALL_RESOURCES.find((r) => r.id === normalized)?.label ?? normalized;
}

export function getConflictingResourceIds(resourceId: string): string[] {
  const normalized = normalizeResourceId(resourceId);

  if (normalized === VIP_COMBINED_RESOURCE_ID) {
    return [...VIP_SPLIT_RESOURCE_IDS, VIP_COMBINED_RESOURCE_ID];
  }
  if (VIP_SPLIT_RESOURCE_IDS.has(normalized)) {
    return [normalized, VIP_COMBINED_RESOURCE_ID];
  }
  return [normalized];
}
